import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Clock } from 'lucide-react';
import { useData } from '../context/DataContext';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import FAQ from '../components/FAQ';

const FeaturedProductCard: React.FC<{ product: any }> = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const productSlug = product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

  // Combine primary image with additional images
  const images = [];
  if (product.image) {
    images.push(product.image);
  }
  if (product.images && product.images.length > 0) {
    images.push(...product.images);
  }

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
      <Link to={`/products/${product.id}/${productSlug}`} className="block">
        <div className="aspect-square md:aspect-[4/3] bg-gray-200 overflow-hidden relative group">
          {images.length > 0 ? (
            <>
              <div className="relative w-full h-full">
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${product.name} - Image ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                  />
                ))}
              </div>
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                    aria-label="Previous image"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                    aria-label="Next image"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => handleDotClick(e, index)}
                        className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex
                            ? 'bg-white w-4'
                            : 'bg-white/50 hover:bg-white/75'
                          }`}
                        aria-label={`View image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-keystone-blue to-keystone-blue-700 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="text-5xl font-bold mb-2">{product.name.charAt(0)}</div>
                <div className="text-sm opacity-75">{product.category || 'Product'}</div>
              </div>
            </div>
          )}
        </div>
      </Link>
      <div className="p-6">
        <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
        <p className="text-gray-600 mb-4 line-clamp-2">
          {product.description}
        </p>
        <div className="flex items-center justify-end">
          <Link
            to={`/quote?product=${product.id}`}
            className="text-keystone-blue hover:text-keystone-blue-700 font-semibold inline-flex items-center"
          >
            Get Quote
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const { featuredProducts, categories } = useData();
  return (
    <div>
      <SEO
        title="Professional Sign & Banner Printing - Keystone Sign Printing"
        description="Get high-quality banners, signs, decals, displays, and stickers for your business in Montgomery County. Fast turnaround, competitive prices. Call (267) 718-3596"
      />
      <StructuredData type="LocalBusiness" />
      <section className="bg-keystone-blue text-white py-10 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Professional Sign and Banner Printing</h1>
          <h2 className="text-4xl font-bold mb-6">Serving Montgomery County and surrounding areas</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Get high-quality banners, signs, decals, displays, and stickers for your business.<br />
            If it can be printed on, we can help you personalize it.<br />
            Fast turnaround, competitive prices, and exceptional quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center sm:justify-center">
            <Link
              to="/products"
              className="flex items-center justify-center bg-white text-keystone-blue px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors min-w-[200px]"
            >
              Browse Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/quote"
              className="flex items-center justify-center bg-keystone-gold text-keystone-blue px-6 py-3 rounded-lg font-semibold hover:bg-keystone-gold-600 transition-colors min-w-[200px]"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Keystone Sign Printing?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-keystone-gold-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-keystone-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Fast Turnaround</h3>
              <p className="text-gray-600">
                Most orders completed within 24-48 hours. Rush delivery available.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-keystone-gold-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-keystone-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600">
                Premium materials and state-of-the-art printing technology.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-keystone-gold-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-keystone-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Online Ordering</h3>
              <p className="text-gray-600">
                Get instant quotes and place orders 24/7 from anywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Shop by Category</h2>
          {(() => {
            // Get active categories
            const activeCategories = categories
              .filter(c => c.active !== false)
              .sort((a, b) => {
                if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
                  return a.displayOrder - b.displayOrder;
                }
                if (a.displayOrder !== undefined) return -1;
                if (b.displayOrder !== undefined) return 1;
                return a.name.localeCompare(b.name);
              });

            // Show up to 8 categories
            let displayCategories = activeCategories.slice(0, 8);

            if (displayCategories.length === 0) {
              // Fallback display if no categories exist
              return (
                <div className="text-center text-gray-500">
                  <p>No categories available at this time.</p>
                </div>
              );
            }

            // Determine grid class based on number of categories
            let gridClass = 'grid grid-cols-1 md:grid-cols-2 gap-6';
            if (displayCategories.length === 1) {
              gridClass = 'grid grid-cols-1 gap-6 max-w-md mx-auto';
            } else if (displayCategories.length === 2) {
              gridClass = 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto';
            } else if (displayCategories.length === 3) {
              gridClass = 'grid grid-cols-1 md:grid-cols-3 gap-6';
            } else {
              gridClass = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6';
            }

            return (
              <div className={gridClass}>
                {displayCategories.map((category, index) => {
                  // Rotate through colors if category doesn't have a specific gradient
                  const gradients = [
                    'from-blue-500 to-blue-600',
                    'from-green-500 to-green-600',
                    'from-purple-500 to-purple-600',
                    'from-orange-500 to-orange-600',
                    'from-pink-500 to-pink-600',
                    'from-red-500 to-red-600',
                    'from-cyan-500 to-cyan-600',
                    'from-indigo-500 to-indigo-600'
                  ];
                  const gradient = gradients[index % gradients.length];

                  return (
                    <Link key={category.id} to={`/products?category=${encodeURIComponent(category.name)}`} className="group block">
                      {category.image ? (
                        <div className="relative rounded-lg overflow-hidden h-full min-h-[220px] transform transition-transform group-hover:scale-105 group-hover:shadow-xl">
                          <img
                            src={category.image}
                            alt={category.name}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                            <h3 className="text-xl font-semibold mb-2">{category.name}</h3>
                            {category.description && (
                              <p className="text-sm opacity-90">{category.description}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`bg-gradient-to-br ${gradient} rounded-lg p-8 text-white text-center transform transition-transform group-hover:scale-105 group-hover:shadow-xl h-full flex flex-col justify-center min-h-[220px]`}>
                          <h3 className="text-xl font-semibold mb-2">{category.name}</h3>
                          {category.description && (
                            <p className="text-sm opacity-90">{category.description}</p>
                          )}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Featured Products</h2>
          {(() => {
            // Use pre-selected featured products from build data
            const activeProducts = featuredProducts;

            // Only show this section if we have products
            if (!activeProducts || activeProducts.length === 0) return null;

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {activeProducts.map(product => (
                  <FeaturedProductCard key={product.id} product={product} />
                ))}
              </div>
            );
          })()}

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center bg-keystone-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-keystone-blue-700 transition-colors"
            >
              View All Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <FAQ items={[
            {
              question: "What types of signs and banners do you print?",
              answer: "We print a wide variety of signs including vinyl banners, yard signs, foam core signs, aluminum signs, decals, stickers, and more. We can handle both indoor and outdoor signage needs for businesses, events, and personal use."
            },
            {
              question: "What's your typical turnaround time?",
              answer: "Most orders are completed within 24-48 hours. For larger or more complex orders, it may take 3-5 business days. Rush delivery is available for urgent needs - just let us know your deadline and we'll do our best to accommodate."
            },
            {
              question: "Do you offer design services?",
              answer: "While we primarily work with print-ready files, we can provide basic design assistance and file preparation. For best results, we recommend providing high-resolution PDF, AI, or EPS files. We accept most common file formats."
            },
            {
              question: "What are your minimum order quantities?",
              answer: "We have no minimum order requirements for most products. Whether you need one banner or one hundred, we're happy to help. Some specialty items may have minimums, which will be clearly indicated during the quote process."
            },
            {
              question: "Do you ship or is it pickup only?",
              answer: "We offer both pickup from our Lansdale, PA location and shipping throughout the United States. Local delivery is also available for larger orders in the Montgomery County area."
            },
            {
              question: "What file formats do you accept?",
              answer: "We accept most common file formats including PDF, AI, EPS, PSD, PNG, and JPG. For best print quality, we recommend vector files (AI, EPS, PDF) or high-resolution raster files (300 DPI or higher)."
            },
            {
              question: "Can I see a proof before printing?",
              answer: "Yes! We provide digital proofs for all orders before printing. You'll have the opportunity to review and approve the proof to ensure everything looks perfect before we proceed with production."
            },
            {
              question: "What's the difference between indoor and outdoor materials?",
              answer: "Outdoor materials are weather-resistant and UV-protected to withstand sun, rain, and wind. Indoor materials are typically lighter weight and more economical but not suitable for outdoor use. We'll help you choose the right material for your specific needs."
            }
          ]} />
        </div>
      </section>
    </div>
  );
};

export default Home;